import { fetchSecurityImage, isClickS3Url } from '@common/services/featherIO';
import { useEffect, useState } from 'react';

/**
 * A stored value re-signed at render time, when - and only when - it names one
 * of our own S3 buckets.
 *
 * Uploads are stored as pre-signed URLs valid for five minutes, so any surface
 * that renders a saved one is usually rendering an expired link.
 *
 * The bucket-name test is deliberately narrower than `isS3Path`, which treats
 * *any* scheme-less string as a bare S3 key. Elements that accept a plain
 * address like "example.com" would otherwise have it signed into a URL for an
 * object that does not exist.
 *
 * `ready` is false only while there is something to sign, so a caller can hold
 * its first paint back rather than showing the expired value and then swapping
 * it. A signing failure falls back to the stored value instead of blocking the
 * surface forever.
 */
export const useSignedS3Url = (
  value: string
): { value: string; ready: boolean } => {
  const [resolved, setResolved] = useState(() => ({
    value,
    ready: !(Boolean(value) && isClickS3Url(value)),
  }));

  useEffect(() => {
    if (!value || !isClickS3Url(value)) {
      setResolved({ value, ready: true });
      return;
    }

    let cancelled = false;
    setResolved({ value, ready: false });

    fetchSecurityImage(value)
      .then((fresh) => {
        if (!cancelled) setResolved({ value: fresh, ready: true });
      })
      .catch(() => {
        if (!cancelled) setResolved({ value, ready: true });
      });

    return () => {
      cancelled = true;
    };
  }, [value]);

  return resolved;
};

export default useSignedS3Url;
